import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { alpha } from "@mui/material/styles";
import {
  Grid,
  Button,
  Container,
  Typography,
  Avatar,
  Box,
  useMediaQuery,
  Stack,
  Alert,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectCurrentUserData,
  updateUserData,
} from "../features/auth/authSlice";
import useAuth from "../hooks/useAuth";
import UserRolesBadge from "../components/rolesbadge/UserRolesBadge";
import {
  useUpdateUserdataAsUserMutation,
  useUploadProfileImageMutation,
} from "../features/users/usersApiSlice";
import DEDateField from "../components/form/fields/DEDateField";
import TextField from "../components/form/fields/TextField";
import SelectField from "../components/form/fields/SelectField";
import { anredeWithBlankArray } from "../components/form/fields/inputFields";
import { serverURL } from "../app/api/axios/http.service";

export default function ProfilePage() {
  const currentUserData = useSelector(selectCurrentUserData);
  const currentUserMail = useSelector(selectCurrentUser);
  const { status } = useAuth();
  const dispatch = useDispatch();
  const [updateUser, { isLoading, isSuccess, isError }] =
    useUpdateUserdataAsUserMutation();
  const [uploadProfileImage, { isLoading: isUploading }] =
    useUploadProfileImageMutation();

  const { handleSubmit, control } = useForm({
    defaultValues: {
      ...currentUserData,
      mail: currentUserMail,
    },
    mode: "onTouched",
  });
  const [profileImage, setProfileImage] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageSuccess, setImageSuccess] = useState("");
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const profileImageUrl = useMemo(() => {
    if (!currentUserData?.id || !currentUserData?.profileImagePath) return "";
    return `${serverURL()}/public/user/image/${currentUserData.id}`;
  }, [currentUserData]);

  const displayName =
    currentUserData?.firstName && currentUserData?.lastName
      ? `${currentUserData.firstName} ${currentUserData.lastName}`
      : currentUserMail || "Benutzerkonto";
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  useEffect(() => {
    if (!profileImageUrl) return;
    if (!profileImage || !profileImage.startsWith("blob:")) {
      setProfileImage(profileImageUrl);
    }
  }, [profileImageUrl, profileImage]);

  useEffect(
    () => () => {
      if (profileImage?.startsWith("blob:")) {
        URL.revokeObjectURL(profileImage);
      }
    },
    [profileImage],
  );

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageError("");
    setImageSuccess("");

    if (!file.type.startsWith("image/")) {
      setImageError("Bitte wählen Sie eine Bilddatei aus.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Die Datei ist zu groß (max. 5 MB).");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setProfileImage(previewUrl);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const result = await uploadProfileImage(formData).unwrap();
      if (result?.profileImagePath) {
        dispatch(updateUserData({ profileImagePath: result.profileImagePath }));
        if (currentUserData?.id) {
          setProfileImage(
            `${serverURL()}/public/user/image/${currentUserData.id}`,
          );
        }
        setImageSuccess("Profilbild erfolgreich aktualisiert.");
      }
    } catch (error) {
      console.error("Error uploading profile image:", error);
      setImageError("Upload fehlgeschlagen. Bitte versuchen Sie es erneut.");
    }
  };

  const onSubmit = async (data) => {
    const uD = {
      title: data.title,
      firstName: data.firstName,
      lastName: data.lastName,
      birthdate: data.birthdate?.$d || null,
      phone: data.phone,
    };

    try {
      // Dispatch the update user mutation
      const result = await updateUser(uD).unwrap();
      dispatch(
        updateUserData({
          title: uD.title,
          firstName: uD.firstName,
          lastName: uD.lastName,
          birthdate: uD.birthdate,
          phone: uD.phone,
        }),
      );

      // Handle success
      console.log("User updated successfully:", result);
    } catch (error) {
      // Handle error
      console.error("Error updating user:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title> Profil | Mitgliederverwaltung</title>
      </Helmet>

      <Container>
        <Stack spacing={0.5}>
          <Typography variant="h4">Mein Profil</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Verwalten Sie Ihre persönlichen Daten und Ihr Profilbild.
          </Typography>
        </Stack>
      </Container>
      <Container>
        <Grid container spacing={3} sx={{ mt: 1, minHeight: "70vh" }}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  `0 18px 40px rgba(0, 0, 0, ${
                    theme.palette.mode === "dark" ? 0.35 : 0.12
                  })`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -40,
                  left: -60,
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -60,
                  right: -80,
                  width: 240,
                  height: 240,
                  borderRadius: "50%",
                  bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                }}
              />

              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
                id="profile-image-upload"
              />
              <label htmlFor="profile-image-upload" style={{ zIndex: 1 }}>
                <Avatar
                  src={profileImage}
                  alt="Profilbild"
                  children={!profileImage ? initials : undefined}
                  sx={{
                    width: 160,
                    height: 160,
                    cursor: "pointer",
                    border: (theme) =>
                      `3px solid rgba(${theme.palette.primary.mainChannel} / 0.35)`,
                    boxShadow: (theme) =>
                      `0 10px 24px rgba(0, 0, 0, ${
                        theme.palette.mode === "dark" ? 0.4 : 0.2
                      })`,
                    "&:hover": { opacity: 0.85 },
                  }}
                  title="Profilbild ändern"
                />
              </label>
              <Typography variant="h6" sx={{ zIndex: 1 }}>
                {displayName}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", zIndex: 1 }}
              >
                {currentUserMail}
              </Typography>
              <UserRolesBadge status={status} />
              <label htmlFor="profile-image-upload" style={{ zIndex: 1 }}>
                <Button
                  variant="contained"
                  size="medium"
                  disabled={isUploading}
                  onClick={() =>
                    document.getElementById("profile-image-upload").click()
                  }
                >
                  {isUploading ? "Lädt hoch…" : "Bild ändern"}
                </Button>
              </label>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", zIndex: 1 }}
              >
                JPG oder PNG, max. 5 MB
              </Typography>
              {imageSuccess && (
                <Alert severity="success" sx={{ width: "100%", zIndex: 1 }}>
                  {imageSuccess}
                </Alert>
              )}
              {imageError && (
                <Alert severity="error" sx={{ width: "100%", zIndex: 1 }}>
                  {imageError}
                </Alert>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) =>
                  `0 18px 40px rgba(0, 0, 0, ${
                    theme.palette.mode === "dark" ? 0.35 : 0.12
                  })`,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Persönliche Daten
              </Typography>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <SelectField
                      control={control}
                      name="title"
                      label="Anrede"
                      variant="outlined"
                      itemArray={anredeWithBlankArray}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      control={control}
                      name="firstName"
                      label="Vorname"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      control={control}
                      name="lastName"
                      label="Nachname"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DEDateField
                      control={control}
                      name="birthdate"
                      label="Geburtsdatum (optional)"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      control={control}
                      name="mail"
                      label="E-Mail-Adresse"
                      variant="outlined"
                      type="email"
                      disabled
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      control={control}
                      name="phone"
                      label="Rufnummer"
                      variant="outlined"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: isSmallScreen ? "stretch" : "flex-end",
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    type="submit"
                    fullWidth={isSmallScreen}
                  >
                    Änderungen speichern
                  </Button>
                </Box>
              </form>
            </Box>
            <Box sx={{ mt: 2 }}>
              {isLoading && (
                <Alert severity="info">Profil wird gespeichert…</Alert>
              )}
              {isSuccess && (
                <Alert severity="success">
                  Profil erfolgreich aktualisiert.
                </Alert>
              )}
              {isError && (
                <Alert severity="error">
                  Aktualisierung fehlgeschlagen. Bitte prüfen Sie Ihre Angaben
                  und versuchen Sie es erneut.
                </Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
